import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Icon from '../AppIcon';
import { changeLocale } from '../../api/General/account';
import constants from '../../assets/constants';
import { useToast, ToastRenderer } from '../../hooks/useToast';
import { applyDocumentLocale } from '../../utils/documentLocale';
import { getPersistor } from '../../store/redux/store/store';

interface RootState {
  user: {
    isLoggedIn: boolean;
    companyLocales?: string;
  };
}

/**
 * Language switch dropdown in TopBar.
 * Integrates with existing i18n system and persists selection to server.
 */
const LanguageSwitch: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { toast, showToast, dismissToast } = useToast();

  const { isLoggedIn, companyLocales } = useSelector((state: RootState) => state.user);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine allowed languages from company config or default
  const languagesAllowed = companyLocales
    ? String(companyLocales).split(',')
    : ['en', 'ar', 'fr'];

  const languageNames: Record<string, string> = {
    en: 'English',
    ar: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629',
    fr: 'Fran\u00E7ais',
  };

  const handleLanguageChange = (lang: string) => {
    setIsOpen(false);
    if (i18n.language === lang) return;

    // Apply the switch immediately — the UI must not wait on (or be blocked by) the server
    // round-trip. A slow or failed persist previously left the picker doing nothing.
    i18n.changeLanguage(lang);
    dispatch({ type: 'SET_LOCALE', payload: { locale: lang } });
    moment.locale(lang);

    // `<html lang>` / `<html dir>`, set HERE and synchronously. App.tsx's effect also applies
    // them, but an effect runs after i18next/redux have settled and the ar → en direction was
    // measured leaving `lang="ar"` on the document. Doing it in the click handler makes the
    // document match the user's choice before anything can await.
    applyDocumentLocale(lang);

    // FLUSH THE PERSIST WRITE. `redux-persist` schedules its localStorage write on a timer, and
    // both readers of the persisted locale run at BOOT — i18n.js (`initialLng`, i18n.js:14-28)
    // and main.tsx's pre-mount `applyDocumentLocale` — off the same `persist:root` key. A reload
    // fired immediately after the click therefore re-read the OLD locale and the switch appeared
    // to be forgotten. `flush()` forces the write out now; it resolves after storage is written,
    // and a failure here is not worth a user-visible error (the switch still holds for the
    // session, which is exactly what the toast below promises).
    try {
      void getPersistor()?.flush?.()?.catch?.(() => {});
    } catch { /* persistor not available (tests / SSR) — the session switch still applied */ }

    // Handle Arabic locale with custom number format
    if (constants.rtlLangs.indexOf(lang) > -1 && lang === 'ar') {
      moment.defineLocale('ar-arNum', {
        parentLocale: 'ar',
        preparse: (str: string) => str,
        postformat: (str: string) => str,
      });
      moment.locale('ar-arNum');
    }

    // Persist to the server in the background; surface a toast if it fails but keep the
    // visible switch (it still applies for this session).
    if (isLoggedIn) {
      changeLocale(lang).catch((error) => {
        console.error('Failed to persist locale:', error);
        showToast(
          t('Could not save your language preference. It will apply for this session.'),
          'warning',
        );
      });
    }
  };

  return (
    <>
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        title={t('Change Language') || 'Change Language'}
      >
        <Icon name="Globe" size={20} className="text-muted-foreground" />
      </button>

      {isOpen && (
        <div className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-40 bg-popover rounded-lg shadow-lg border border-border z-50">
          <div className="py-1">
            {languagesAllowed.map((lang) => (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className={`w-full px-4 py-2 min-h-[44px] text-sm text-start hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  i18n.language === lang ? 'bg-primary/5 text-primary font-medium' : 'text-foreground'
                }`}
              >
                {languageNames[lang] || lang}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
    <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </>
  );
};

export default LanguageSwitch;
